/**
 * PromotionBanner — thin site-wide teaser bar for active campaigns.
 *
 * Driven off the same GET /api/promotions/active-terms as the terms
 * section. Shows nothing when no campaign is active. When multiple
 * campaigns run simultaneously, rotates every ~6 s so none get hidden.
 *
 * Link target: /promotion-terms (dedicated page hosting
 * PromotionTermsSection). Kept internal (React Router) so the SPA
 * transition stays smooth.
 *
 * Stacking / layout contract (fixes the "banner hides behind nav" bug):
 *  1. The banner is `fixed top-0 z-[60]` — one step above the site
 *     nav's `z-50`. Being fixed puts it in the top viewport layer
 *     regardless of DOM position or ancestor stacking contexts (e.g.
 *     a Starfield with `transform` used to trap the previous
 *     non-positioned banner underneath).
 *  2. Its rendered height is measured live (ResizeObserver) and
 *     published as CSS variable `--promo-banner-h` on <html>. Any
 *     other fixed/sticky element (LandingPage nav, BookingPage
 *     sticky header, page-content padding) can read this var and
 *     shift down by exactly the right amount without prop-drilling.
 *  3. Var goes back to `0px` when the banner is empty / unmounted so
 *     pages with no active promo don't get phantom top padding.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { API } from "../App";

const rotationMs = 6000;
const dismissKey = "promo_banner_dismissed_ids"; // sessionStorage — session-scoped only

const setBannerHeightVar = (px) => {
  if (typeof document !== "undefined") {
    document.documentElement.style.setProperty("--promo-banner-h", `${px}px`);
  }
};

const PromotionBanner = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(dismissKey) || "[]"));
    } catch { return new Set(); }
  });
  const barRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/promotions/active-terms`)
      .then((r) => {
        if (cancelled) return;
        // HARDENING: any non-array / malformed shape → treat as empty.
        // A broken promo endpoint must NEVER show placeholder text to
        // real visitors. The only valid render path is a well-formed
        // campaign object with campaign_id + name.
        const raw = Array.isArray(r?.data) ? r.data : [];
        const clean = raw.filter(
          (c) =>
            c &&
            typeof c.campaign_id === "string" &&
            typeof c.name === "string" &&
            c.name.trim() &&
            // Defence-in-depth: promo_code campaigns are word-of-mouth
            // only and must never render in the banner. Backend
            // /active-terms already filters these out at the query,
            // but if a legacy row slips through we still refuse to
            // paint it here.
            c.mechanic_type !== "promo_code",
        );
        setCampaigns(clean);
      })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, []);

  // Filter out dismissed-this-session campaigns.
  const visible = campaigns.filter((c) => !dismissed.has(c.campaign_id));

  // Rotate when there are 2+ visible; single or none → no rotation.
  useEffect(() => {
    if (visible.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), rotationMs);
    return () => clearInterval(t);
  }, [visible.length]);

  // Keep idx in range if list shrinks.
  useEffect(() => {
    if (idx >= visible.length && visible.length > 0) setIdx(0);
  }, [visible.length, idx]);

  // Publish rendered height as a CSS var so the nav / sticky header /
  // page content can push down by exactly the banner height. On
  // desktop it's ~36px; on mobile it can wrap to ~56px, so
  // ResizeObserver keeps it accurate across viewport changes.
  useEffect(() => {
    if (visible.length === 0 || !barRef.current) {
      setBannerHeightVar(0);
      return;
    }
    const update = () => {
      const el = barRef.current;
      if (!el) return;
      setBannerHeightVar(Math.round(el.getBoundingClientRect().height));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(barRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      setBannerHeightVar(0);
    };
  }, [visible.length]);

  const dismiss = useCallback(() => {
    if (visible.length === 0) return;
    const current = visible[Math.min(idx, visible.length - 1)];
    const next = new Set(dismissed);
    next.add(current.campaign_id);
    setDismissed(next);
    try {
      sessionStorage.setItem(dismissKey, JSON.stringify(Array.from(next)));
    } catch { /* private-mode / storage full — silently ignored */ }
  }, [dismissed, idx, visible]);

  if (visible.length === 0) return null;

  const current = visible[Math.min(idx, visible.length - 1)];
  const svc =
    current.applicable_service_names && current.applicable_service_names.length > 0
      ? current.applicable_service_names[0]
      : "a service";

  return (
    <div
      ref={barRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-slate-900 shadow-md"
      data-testid="promotion-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm font-semibold">
        <Sparkles className="w-4 h-4 flex-shrink-0" />
        <span className="text-center leading-tight" data-testid={`promotion-banner-name-${current.campaign_id}`}>
          <strong>{current.name}</strong> is live — book {svc} now.{" "}
          <Link
            to="/promotion-terms"
            className="underline underline-offset-2 hover:text-slate-950 whitespace-nowrap"
            data-testid="promotion-banner-terms-link"
          >
            See terms
          </Link>
        </span>
        {visible.length > 1 && (
          <span className="hidden sm:inline text-slate-800 text-xs opacity-70 ml-2"
                data-testid="promotion-banner-rotation-indicator">
            {idx + 1}/{visible.length}
          </span>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss banner"
          className="ml-1 sm:ml-2 hover:bg-slate-900/10 rounded p-1 transition-colors flex-shrink-0"
          data-testid="promotion-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PromotionBanner;
